import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import LoginScreen from './components/LoginScreen';
import SpecEditor from './components/SpecEditor';
import RulesPanel from './components/RulesPanel';
import ValidationPanel from './components/ValidationPanel';
import SuggestionsPanel from './components/SuggestionsPanel';

import TemplateSelector from './components/TemplateSelector';
import PreviewModal from './components/PreviewModal';
import SearchModal from './components/SearchModal';
import SchemaBuilderModal from './components/SchemaBuilderModal';
import JsonToSchemaModal from './components/JsonToSchemaModal';
import MockServerModal from './components/MockServerModal';
import ImportModal from './components/ImportModal';
import SpecsLibrary from './components/SpecsLibrary';
import SaveSpecModal from './components/SaveSpecModal';
import VersionManager from './components/VersionManager';
import PublicSpecViewer from './components/PublicSpecViewer';
import UserManagement from './components/UserManagement';
import ExportModal from './components/ExportModal';
import CommandPalette from './components/CommandPalette';
import TestEndpointModal from './components/TestEndpointModal';
import SplitViewToggle from './components/SplitViewToggle';
import ConfirmModal from './components/ConfirmModal';
import SdkGeneratorModal from './components/SdkGeneratorModal';
import EditMetadataModal from './components/EditMetadataModal';
import RulesConfigModal from './components/RulesConfigModal';
import { useConfirm } from './hooks/useConfirm';
import { defaultRules } from './utils/defaultRules';
import { generateSuggestions } from './utils/suggestions';
import { templates } from './utils/templates';
import yaml from 'js-yaml';
import * as api from './utils/api';

const Container = styled.div`
  display: flex;
  height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: ${props => props.$darkMode ? '#0d1117' : '#ffffff'};
  color: ${props => props.$darkMode ? '#c9d1d9' : '#24292f'};
  overflow: hidden;
`;

const MainPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

const PanelToggle = styled.button`
  position: fixed;
  right: ${props => props.$collapsed ? '10px' : props.$width + 10 + 'px'};
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.$darkMode ? '#21262d' : '#f6f8fa'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 4px;
  padding: 8px 6px;
  cursor: pointer;
  z-index: 1000;
  color: ${props => props.$darkMode ? '#c9d1d9' : '#24292f'};
  font-size: 14px;
  transition: right 0.2s ease;
  
  &:hover {
    background: ${props => props.$darkMode ? '#30363d' : '#e1e4e8'};
  }
  
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-right: 8px;
    background: rgba(0,0,0,0.9);
    color: white;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
  }
`;

const SidePanel = styled.div`
  width: ${props => props.$collapsed ? '0' : props.$width + 'px'};
  min-width: ${props => props.$collapsed ? '0' : '200px'};
  max-width: ${props => props.$collapsed ? '0' : '800px'};
  border-left: ${props => props.$collapsed ? 'none' : `1px solid ${props.$darkMode ? '#30363d' : '#e1e4e8'}`};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${props => props.$darkMode ? '#0d1117' : '#ffffff'};
  position: relative;
`;


const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  z-index: 1000;
  
  &:hover {
    background: ${props => props.$darkMode ? '#58a6ff' : '#0969da'};
  }
  
  &:active {
    background: ${props => props.$darkMode ? '#58a6ff' : '#0969da'};
  }
`;



const Header = styled.div`
  padding: 16px 20px 0 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-bottom: 2px solid #5568d3;
  flex-shrink: 0;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  width: 100%;
  gap: 20px;
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 10px 16px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  margin-bottom: 12px;
  
  &::placeholder {
    color: rgba(255,255,255,0.7);
  }
  
  &:focus {
    outline: none;
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.5);
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  gap: 12px;
  
  img {
    height: 32px;
    width: auto;
  }
`;

const Subtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 13px;
  color: rgba(255,255,255,0.9);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleLabel = styled.span`
  font-size: 12px;
  color: rgba(255,255,255,0.9);
  font-weight: 500;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #28a745;
  }
  
  &:checked + span:before {
    transform: translateX(20px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.$darkMode ? '#30363d' : 'rgba(0,0,0,0.2)'};
  transition: 0.3s;
  border-radius: 24px;
  border: 1px solid ${props => props.$darkMode ? '#484f58' : 'rgba(0,0,0,0.3)'};
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: ${props => props.$darkMode ? '#8b949e' : 'white'};
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const Button = styled.button`
  padding: 6px 12px;
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  position: relative;
  
  &:hover {
    background: rgba(255,255,255,0.3);
    transform: translateY(-1px);
  }
  
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: -35px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0,0,0,0.9);
    color: white;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
    z-index: 1000;
  }
`;

const ToggleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: -35px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0,0,0,0.9);
    color: white;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
    z-index: 1000;
  }
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const Menu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  overflow: hidden;
  z-index: 1000;
  min-width: 140px;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  background: white;
  border: none;
  text-align: left;
  font-size: 13px;
  color: #24292f;
  cursor: pointer;
  transition: background 0.15s;
  
  &:hover {
    background: #f6f8fa;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e1e4e8;
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  margin-left: 12px;
  
  &:hover {
    background: #218838;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

function App() {
  const { confirm, alert: showAlert, modal } = useConfirm();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('auth-token');
    if (!token) return false;
    
    try {
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1]));
      return Date.now() < payload.exp * 1000;
    } catch (e) {
      return false;
    }
  });
  
  const [showTemplateSelector, setShowTemplateSelector] = useState(() => {
    const hash = window.location.hash.slice(1);
    if (hash) return false;
    return !localStorage.getItem('openapi-spec');
  });
  const [previewType, setPreviewType] = useState(null);
  const [showPreviewMenu, setShowPreviewMenu] = useState(false);
  const [showFileMenu, setShowFileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSchemaBuilder, setShowSchemaBuilder] = useState(false);
  const [showJsonToSchema, setShowJsonToSchema] = useState(false);
  const [showMockServer, setShowMockServer] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showSaveSpec, setShowSaveSpec] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showVersionManager, setShowVersionManager] = useState(false);
  const [currentSpecId, setCurrentSpecId] = useState(() => {
    const saved = localStorage.getItem('current-spec-id');
    return saved ? parseInt(saved) : null;
  });
  const [currentSpecName, setCurrentSpecName] = useState(() => localStorage.getItem('current-spec-name'));
  const [currentSpecInfo, setCurrentSpecInfo] = useState(null);
  const [currentVersionId, setCurrentVersionId] = useState(() => {
    const saved = localStorage.getItem('current-version-id');
    return saved ? parseInt(saved) : null;
  });
  const [currentVersionIsLive, setCurrentVersionIsLive] = useState(() => localStorage.getItem('current-version-live') === 'true');
  const [currentVersionIsPublic, setCurrentVersionIsPublic] = useState(() => localStorage.getItem('current-version-public') === 'true');
  const [currentUser, setCurrentUser] = useState(() => {
    return JSON.parse(localStorage.getItem('current-user') || '{"role":"editor"}');
  });
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showTestEndpoint, setShowTestEndpoint] = useState(false);
  const [showSdkGenerator, setShowSdkGenerator] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showComponentsMenu, setShowComponentsMenu] = useState(false);
  const [showEditMetadata, setShowEditMetadata] = useState(false);
  const [showRulesConfig, setShowRulesConfig] = useState(false);
  const [splitView, setSplitView] = useState(() => {
    return localStorage.getItem('splitView') === 'true';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [panelStates, setPanelStates] = useState({ validation: false, suggestions: false });
  const [panelOrder, setPanelOrder] = useState(['validation', 'suggestions']);
  const [draggedPanel, setDraggedPanel] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(350);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  
  const [spec, setSpec] = useState(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      try {
        return atob(hash);
      } catch (e) {}
    }
    return localStorage.getItem('openapi-spec') || templates.scratch;
  });
  const [rules, setRules] = useState(defaultRules);
  const [validationErrors, setValidationErrors] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(() => {
    return localStorage.getItem('advancedMode') === 'true';
  });
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [format, setFormat] = useState(() => {
    return localStorage.getItem('format') || 'yaml';
  });

  
  useEffect(() => {
    try {
      const parsed = yaml.load(spec);
      setCurrentSpecInfo({
        title: parsed.info?.title || 'Untitled',
        version: parsed.info?.version || '1.0.0'
      });
    } catch (e) {
      setCurrentSpecInfo(null);
    }
  }, [spec]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      const newSuggestions = generateSuggestions(spec);
      setSuggestions(newSuggestions);
    }, 1000);
    return () => clearTimeout(timer);
  }, [spec]);
  
  useEffect(() => {
    localStorage.setItem('advancedMode', advancedMode);
  }, [advancedMode]);
  
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);
  
  useEffect(() => {
    localStorage.setItem('format', format);
  }, [format]);



  useEffect(() => {
    localStorage.setItem('splitView', splitView);
  }, [splitView]);

  useEffect(() => {
    if (currentSpecId) {
      localStorage.setItem('current-spec-id', currentSpecId);
    } else {
      localStorage.removeItem('current-spec-id');
    }
  }, [currentSpecId]);

  useEffect(() => {
    if (currentSpecName) {
      localStorage.setItem('current-spec-name', currentSpecName);
    } else {
      localStorage.removeItem('current-spec-name');
    }
  }, [currentSpecName]);

  useEffect(() => {
    if (currentVersionId) {
      localStorage.setItem('current-version-id', currentVersionId);
    } else {
      localStorage.removeItem('current-version-id');
    }
  }, [currentVersionId]);

  useEffect(() => {
    localStorage.setItem('current-version-live', currentVersionIsLive);
  }, [currentVersionIsLive]);

  useEffect(() => {
    localStorage.setItem('current-version-public', currentVersionIsPublic);
  }, [currentVersionIsPublic]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdKey = isMac ? e.metaKey : e.ctrlKey;
      
      if (cmdKey && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCommand = (commandId) => {
    setShowCommandPalette(false);
    switch (commandId) {
      case 'new': setShowTemplateSelector(true); break;
      case 'import': setShowImport(true); break;
      case 'export': setShowExport(true); break;
      case 'search': setShowSearch(true); break;
      case 'preview': setShowPreviewMenu(true); break;
      case 'schema-builder': setShowSchemaBuilder(true); break;
      case 'json-to-schema': setShowJsonToSchema(true); break;
      case 'mock-server': setShowMockServer(true); break;
      case 'test-endpoint': setShowTestEndpoint(true); break;
      case 'generate-sdk': setShowSdkGenerator(true); break;
      case 'toggle-dark': setDarkMode(!darkMode); break;
      case 'toggle-format': setFormat(format === 'yaml' ? 'json' : 'yaml'); break;
      case 'toggle-split': setSplitView(!splitView); break;
      case 'user-management': setShowUserManagement(true); break;
      case 'logout': handleLogout(); break;
      case 'format': break;
      case 'copy': navigator.clipboard.writeText(spec); showAlert('Spec copied to clipboard!'); break;
      case 'share': 
        const url = window.location.origin + window.location.pathname + '#' + btoa(spec);
        navigator.clipboard.writeText(url);
        showAlert('Share URL copied to clipboard!');
        break;
    }
  };

  const handleSpecChange = useCallback((newSpec) => {
    setSpec(newSpec);
    localStorage.setItem('openapi-spec', newSpec);
    window.history.replaceState(null, '', '#' + btoa(newSpec));
  }, []);

  const handleRulesChange = useCallback((newRules) => {
    setRules(newRules);
  }, []);

  const handleValidationChange = useCallback((errors) => {
    setValidationErrors(errors);
  }, []);
  
  const handleTemplateSelect = (templateId) => {
    const templateSpec = templates[templateId];
    setSpec(templateSpec);
    localStorage.setItem('openapi-spec', templateSpec);
    setShowTemplateSelector(false);
  };
  
  const [isGuestMode, setIsGuestMode] = useState(false);
  
  const handleLogin = (user, token) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    setIsGuestMode(user.email === 'guest@local');
  };
  
  const handleLogout = () => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('current-user');
    setIsAuthenticated(false);
    setCurrentUser({ role: 'viewer' });
  };
  
  const handleSidebarMouseDown = () => setIsResizingSidebar(true);
  
  const handleSidebarMouseMove = (e) => {
    if (!isResizingSidebar) return;
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= 200 && newWidth <= 800) {
      setSidebarWidth(newWidth);
    }
  };
  
  const handleSidebarMouseUp = () => setIsResizingSidebar(false);
  
  React.useEffect(() => {
    if (isResizingSidebar) {
      document.addEventListener('mousemove', handleSidebarMouseMove);
      document.addEventListener('mouseup', handleSidebarMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleSidebarMouseMove);
        document.removeEventListener('mouseup', handleSidebarMouseUp);
      };
    }
  }, [isResizingSidebar]);
  
  // Public spec viewer route (no auth required)
  if (window.location.pathname.startsWith('/public/specs/')) {
    return <PublicSpecViewer />;
  }

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <>
      {modal && <ConfirmModal {...modal} darkMode={darkMode} />}
      {showTemplateSelector && <TemplateSelector onSelect={handleTemplateSelect} />}
      {previewType && <PreviewModal type={previewType} spec={spec} onClose={() => setPreviewType(null)} />}
      {showSearch && <SearchModal spec={spec} format={format} onClose={() => setShowSearch(false)} onJumpToLine={(line) => window.jumpToLine && window.jumpToLine(line)} />}

      {showSchemaBuilder && <SchemaBuilderModal spec={spec} onSave={(newSpec) => { setSpec(newSpec); setShowSchemaBuilder(false); }} onClose={() => setShowSchemaBuilder(false)} />}
      {showJsonToSchema && <JsonToSchemaModal spec={spec} onSave={(newSpec) => { setSpec(newSpec); setShowJsonToSchema(false); }} onClose={() => setShowJsonToSchema(false)} />}
      {showMockServer && <MockServerModal spec={spec} onClose={() => setShowMockServer(false)} />}
      {showImport && <ImportModal onImport={(newSpec) => { setSpec(newSpec); setShowImport(false); }} onClose={() => setShowImport(false)} />}
      {showSaveSpec && <SaveSpecModal spec={spec} darkMode={darkMode} onClose={() => setShowSaveSpec(false)} onSave={(savedSpec) => { 
        setCurrentSpecId(savedSpec.id); 
        setCurrentSpecName(savedSpec.spec_name); 
        localStorage.setItem('current-spec-id', savedSpec.id);
        localStorage.setItem('current-spec-name', savedSpec.spec_name);
        showAlert('Spec saved successfully!'); 
      }} />}
      {showVersionManager && currentSpecId && <VersionManager specId={currentSpecId} specName={currentSpecName} onClose={() => setShowVersionManager(false)} onSelectVersion={(version) => { handleSpecChange(version.content); setShowVersionManager(false); }} />}
      {showLibrary && <SpecsLibrary darkMode={darkMode} onClose={() => setShowLibrary(false)} onLoad={(savedSpec, action, versionData) => {
        setShowLibrary(false);
        if (action === 'edit') {
          handleSpecChange(savedSpec.spec);
          setCurrentSpecId(savedSpec.id);
          setCurrentSpecName(savedSpec.spec_name || savedSpec.title);
          if (versionData) {
            setCurrentVersionId(versionData.id);
            setCurrentVersionIsLive(versionData.is_live === 1);
            setCurrentVersionIsPublic(versionData.is_public === 1);
            localStorage.setItem('current-version-id', versionData.id);
            localStorage.setItem('current-version-live', versionData.is_live === 1);
            localStorage.setItem('current-version-public', versionData.is_public === 1);
          } else {
            setCurrentVersionId(null);
            setCurrentVersionIsLive(false);
            setCurrentVersionIsPublic(false);
            localStorage.removeItem('current-version-id');
            localStorage.removeItem('current-version-live');
            localStorage.removeItem('current-version-public');
          }
        } else {
          setPreviewType(action);
        }
      }} />}
      {showUserManagement && <UserManagement darkMode={darkMode} currentUser={currentUser} onClose={() => setShowUserManagement(false)} />}
      {showExport && <ExportModal spec={spec} onClose={() => setShowExport(false)} />}
      {showCommandPalette && <CommandPalette onClose={() => setShowCommandPalette(false)} onCommand={handleCommand} />}
      {showTestEndpoint && <TestEndpointModal spec={spec} onClose={() => setShowTestEndpoint(false)} />}
      {showSdkGenerator && <SdkGeneratorModal spec={spec} darkMode={darkMode} onClose={() => setShowSdkGenerator(false)} />}
      {showEditMetadata && currentSpecId && <EditMetadataModal specId={currentSpecId} darkMode={darkMode} onClose={() => setShowEditMetadata(false)} onSave={(metadata) => { setCurrentSpecName(metadata.spec_name); showAlert('Metadata updated!'); }} />}
      {showRulesConfig && <RulesConfigModal darkMode={darkMode} onClose={() => setShowRulesConfig(false)} isAuthenticated={isAuthenticated && !isGuestMode} isAdmin={currentUser.role === 'admin'} customRules={rules} onCustomRulesChange={handleRulesChange} />}
      {showPreviewMenu && <div onClick={() => setShowPreviewMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />}
      {showFileMenu && <div onClick={() => setShowFileMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />}
      {showToolsMenu && <div onClick={() => setShowToolsMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />}
      {showComponentsMenu && <div onClick={() => setShowComponentsMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />}
      <Container $darkMode={darkMode}>
        <MainPanel>
          <Header>
          <HeaderTop>
            <HeaderLeft>
            <Title>
              <a href="https://deviantdev.co.uk" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                <img src={require('./Dlogomin.png')} alt="Logo" style={{ cursor: 'pointer' }} />
              </a>
              OpenAPI 3.0 Editor
            </Title>
            <Subtitle>
              {currentSpecInfo ? (
                <>
                  <strong>{currentSpecInfo.title}</strong> v{currentSpecInfo.version}
                  {currentSpecId && <span style={{ marginLeft: '12px', padding: '2px 8px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px' }}>SAVED</span>}
                  {!currentSpecId && <span style={{ marginLeft: '12px', padding: '2px 8px', background: 'rgba(255,200,0,0.3)', borderRadius: '4px', fontSize: '11px' }}>UNSAVED</span>}
                  {currentVersionIsLive && <span style={{ marginLeft: '8px', padding: '2px 8px', background: 'rgba(33,150,243,0.3)', borderRadius: '4px', fontSize: '11px' }}>LIVE</span>}
                  {currentVersionIsPublic && <span style={{ marginLeft: '8px', padding: '2px 8px', background: 'rgba(255,152,0,0.3)', borderRadius: '4px', fontSize: '11px' }}>PUBLIC</span>}
                  {currentVersionIsLive && currentVersionId && <button 
                      onClick={async () => {
                        if (await confirm({ type: 'warning', title: 'Unset Live Version?', message: 'This will allow you to edit this version. It will no longer be live or public.' })) {
                          try {
                            await api.updateVersionStatus(currentSpecId, currentVersionId, { is_live: false, is_latest_live: false, is_public: false });
                            setCurrentVersionIsLive(false);
                            setCurrentVersionIsPublic(false);
                            showAlert('Version is now editable!');
                          } catch (e) {
                            showAlert(e.message, 'Error', 'error');
                          }
                        }
                      }}
                      style={{ 
                        padding: '4px 10px', 
                        background: 'rgba(255,255,255,0.2)', 
                        border: '1px solid rgba(255,255,255,0.3)', 
                        borderRadius: '4px', 
                        color: 'white', 
                        fontSize: '11px', 
                        cursor: 'pointer',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        marginLeft: '8px',
                        verticalAlign: 'middle'
                      }}
                    >
                      Unset Live
                    </button>}
                </>
              ) : 'Create and validate OpenAPI specifications'}
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <ToggleContainer>
              <ToggleLabel>YAML</ToggleLabel>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={format === 'json'}
                  onChange={(e) => setFormat(e.target.checked ? 'json' : 'yaml')}
                />
                <ToggleSlider $darkMode={darkMode} />
              </ToggleSwitch>
              <ToggleLabel>JSON</ToggleLabel>
            </ToggleContainer>
            <ToggleWrapper data-tooltip="Dark Mode">
              <div style={{ fontSize: '16px' }}>{darkMode ? '🌙' : '☀️'}</div>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={darkMode}
                  onChange={(e) => setDarkMode(e.target.checked)}
                />
                <ToggleSlider $darkMode={darkMode} />
              </ToggleSwitch>
            </ToggleWrapper>

            <SplitViewToggle splitView={splitView} onToggle={() => setSplitView(!splitView)} />
            <Button onClick={() => setShowRulesConfig(true)} data-tooltip="Configure validation rules">⚙️ Rules</Button>
            <MenuWrapper>
              <Button onClick={() => setShowFileMenu(!showFileMenu)}>📁 File</Button>
              {showFileMenu && (
                <Menu>
                  <MenuItem onClick={() => { setShowTemplateSelector(true); setShowFileMenu(false); }}>
                    🆕 New
                  </MenuItem>
                  <MenuItem onClick={() => { setShowImport(true); setShowFileMenu(false); }}>
                    📥 Import
                  </MenuItem>
                  <MenuItem onClick={() => { setShowExport(true); setShowFileMenu(false); }}>
                    📤 Export
                  </MenuItem>
                  <MenuItem onClick={() => { setShowSaveSpec(true); setShowFileMenu(false); }}>
                    💾 Save Spec
                  </MenuItem>
                  {!isGuestMode && currentSpecId && <MenuItem onClick={() => { setShowEditMetadata(true); setShowFileMenu(false); }}>
                    ✏️ Edit Metadata
                  </MenuItem>}
                  {!isGuestMode && currentSpecId && <MenuItem onClick={() => { setShowVersionManager(true); setShowFileMenu(false); }}>
                    📋 Manage Versions
                  </MenuItem>}
                  {!isGuestMode && currentSpecId && <MenuItem onClick={async () => {
                    try {
                      const parsed = yaml.load(spec);
                      const version = parsed.info?.version || '1.0.0';
                      await api.createSpecVersion(currentSpecId, { version, content: spec });
                      showAlert('New version created successfully!');
                    } catch (e) {
                      showAlert(e.message, 'Error', 'error');
                    }
                    setShowFileMenu(false);
                  }}>
                    💾 Save as New Version
                  </MenuItem>}
                  <MenuItem onClick={() => { setShowLibrary(true); setShowFileMenu(false); }}>
                    📚 Open Library
                  </MenuItem>
                  <MenuItem onClick={async () => {
                    navigator.clipboard.writeText(spec);
                    await showAlert('Spec copied to clipboard!');
                    setShowFileMenu(false);
                  }}>📋 Copy to Clipboard</MenuItem>
                  <MenuItem onClick={async () => {
                    const url = window.location.origin + window.location.pathname + '#' + btoa(spec);
                    navigator.clipboard.writeText(url);
                    await showAlert('Share URL copied to clipboard!');
                    setShowFileMenu(false);
                  }}>🔗 Share URL</MenuItem>
                  {!isGuestMode && <MenuItem onClick={() => { setShowUserManagement(true); setShowFileMenu(false); }}>
                    👤 Users
                  </MenuItem>}
                  {!isGuestMode && <MenuItem onClick={() => { handleLogout(); setShowFileMenu(false); }}>
                    🚪 Logout
                  </MenuItem>}
                </Menu>
              )}
            </MenuWrapper>
            <MenuWrapper>
              <Button onClick={() => setShowComponentsMenu(!showComponentsMenu)} data-tooltip="Add OpenAPI components">➕ Add Component</Button>
              {showComponentsMenu && (
                <Menu style={{ minWidth: '200px', maxHeight: '500px', overflowY: 'auto' }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #e1e4e8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} title="Show modal dialogs to collect component details before insertion">
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#586069' }}>Enable Prompts</span>
                    <ToggleSwitch>
                      <ToggleInput
                        type="checkbox"
                        checked={advancedMode}
                        onChange={(e) => setAdvancedMode(e.target.checked)}
                      />
                      <ToggleSlider $darkMode={darkMode} />
                    </ToggleSwitch>
                  </div>
                  <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#586069', textTransform: 'uppercase', borderBottom: '1px solid #e1e4e8' }}>HTTP Methods</div>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'get-endpoint' }); setShowComponentsMenu(false); }}>GET Endpoint</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'post-endpoint' }); setShowComponentsMenu(false); }}>POST Endpoint</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'put-endpoint' }); setShowComponentsMenu(false); }}>PUT Endpoint</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'patch-endpoint' }); setShowComponentsMenu(false); }}>PATCH Endpoint</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'delete-endpoint' }); setShowComponentsMenu(false); }}>DELETE Endpoint</MenuItem>
                  <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#586069', textTransform: 'uppercase', borderBottom: '1px solid #e1e4e8', marginTop: '4px' }}>Schemas</div>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'object-schema' }); setShowComponentsMenu(false); }}>Object Schema</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'array-schema' }); setShowComponentsMenu(false); }}>Array Schema</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'string-schema' }); setShowComponentsMenu(false); }}>String Schema</MenuItem>
                  <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#586069', textTransform: 'uppercase', borderBottom: '1px solid #e1e4e8', marginTop: '4px' }}>Parameters</div>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'path-parameter' }); setShowComponentsMenu(false); }}>Path Parameter</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'query-parameter' }); setShowComponentsMenu(false); }}>Query Parameter</MenuItem>
                  <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#586069', textTransform: 'uppercase', borderBottom: '1px solid #e1e4e8', marginTop: '4px' }}>Responses</div>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'success-response' }); setShowComponentsMenu(false); }}>200 Success</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'created-response' }); setShowComponentsMenu(false); }}>201 Created</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'badrequest-response' }); setShowComponentsMenu(false); }}>400 Bad Request</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'notfound-response' }); setShowComponentsMenu(false); }}>404 Not Found</MenuItem>
                  <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#586069', textTransform: 'uppercase', borderBottom: '1px solid #e1e4e8', marginTop: '4px' }}>Security</div>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'bearer-auth' }); setShowComponentsMenu(false); }}>Bearer Token</MenuItem>
                  <MenuItem onClick={() => { window.addComponent && window.addComponent({ type: 'api-key-auth' }); setShowComponentsMenu(false); }}>API Key Auth</MenuItem>
                </Menu>
              )}
            </MenuWrapper>
            <Button onClick={() => setShowCommandPalette(true)} data-tooltip="⌘K / Ctrl+K">⌘ Commands</Button>
            <Button onClick={() => setShowSearch(true)} data-tooltip="Search through spec">🔍 Search</Button>
            <MenuWrapper>
              <ActionButton onClick={() => setShowPreviewMenu(!showPreviewMenu)} style={{ background: '#0969da' }}>🔍 Preview</ActionButton>
              {showPreviewMenu && (
                <Menu>
                  <MenuItem onClick={() => { setPreviewType('custom'); setShowPreviewMenu(false); }}>
                    <img src={require('./Dlogomin.png')} alt="" style={{height: '14px', marginRight: '6px', verticalAlign: 'middle'}} /> Deviant Viewer
                  </MenuItem>
                  <MenuItem onClick={() => { setPreviewType('swagger'); setShowPreviewMenu(false); }}>
                    🔍 Swagger UI
                  </MenuItem>
                  <MenuItem onClick={() => { setPreviewType('elements'); setShowPreviewMenu(false); }}>
                    📄 Stoplight Elements
                  </MenuItem>
                  <MenuItem onClick={() => { setPreviewType('redoc'); setShowPreviewMenu(false); }}>
                    📖 Redoc
                  </MenuItem>
                  <MenuItem onClick={() => { setPreviewType('rapidoc'); setShowPreviewMenu(false); }}>
                    ⚡ RapiDoc
                  </MenuItem>
                  <MenuItem onClick={() => { setPreviewType('scalar'); setShowPreviewMenu(false); }}>
                    🔷 Scalar
                  </MenuItem>
                </Menu>
              )}
            </MenuWrapper>
          </HeaderRight>
          </HeaderTop>
        </Header>
        <SpecEditor 
          spec={spec}
          rules={rules}
          advancedMode={advancedMode}
          darkMode={darkMode}
          format={format}
          splitView={splitView}

          onSpecChange={handleSpecChange}
          onValidationChange={handleValidationChange}
          onErrorClick={(line) => window.jumpToLine && window.jumpToLine(line)}
        />
        </MainPanel>
        <PanelToggle 
          $darkMode={darkMode}
          $collapsed={sidebarCollapsed}
          $width={sidebarWidth}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          data-tooltip={sidebarCollapsed ? 'Show Panel' : 'Hide Panel'}
        >
          {sidebarCollapsed ? '◀' : '▶'}
        </PanelToggle>
        <SidePanel $darkMode={darkMode} $collapsed={sidebarCollapsed} $width={sidebarWidth}>
          {!sidebarCollapsed && <ResizeHandle $darkMode={darkMode} onMouseDown={handleSidebarMouseDown} />}
          {panelOrder.map((panelId) => {
            const isCollapsed = panelStates[panelId];
            const commonProps = {
              collapsed: isCollapsed,
              onToggle: () => setPanelStates(prev => ({ ...prev, [panelId]: !prev[panelId] })),
              draggable: true,
              onDragStart: () => setDraggedPanel(panelId),
              onDragOver: (e) => {
                e.preventDefault();
                if (draggedPanel && draggedPanel !== panelId) {
                  const newOrder = [...panelOrder];
                  const draggedIdx = newOrder.indexOf(draggedPanel);
                  const targetIdx = newOrder.indexOf(panelId);
                  newOrder.splice(draggedIdx, 1);
                  newOrder.splice(targetIdx, 0, draggedPanel);
                  setPanelOrder(newOrder);
                }
              },
              onDragEnd: () => setDraggedPanel(null)
            };
            
            if (panelId === 'validation') {
              return <ValidationPanel
                key={panelId}
                {...commonProps}
                errors={validationErrors}
                format={format}
                onFixError={(error) => {
                  if (error.fix) {
                    const oldSpec = spec;
                    const fixedSpec = error.fix();
                    setSpec(fixedSpec);
                    if (window.highlightFix) {
                      window.highlightFix(oldSpec, fixedSpec);
                    }
                  }
                }}
                onErrorClick={(line) => window.jumpToLine && window.jumpToLine(line)}
              />;
            } else if (panelId === 'suggestions') {
              return <SuggestionsPanel
                key={panelId}
                {...commonProps}
                suggestions={suggestions}
                onApplySuggestion={(suggestion) => {
                  if (suggestion.apply) {
                    const updatedSpec = suggestion.apply(spec);
                    setSpec(updatedSpec);
                  }
                }}
              />;
            }
            return null;
          })}
        </SidePanel>
      </Container>
    </>
  );
}

export default App;
